import { useState } from 'react';
import { useSelectedContract, useSelectedCampaign } from 'hooks';
import { Pages } from 'constants/menu';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import {
  AgxAlerts,
  AgxButton,
  AgxSpacer,
  AgxTypography,
  formatCurrency,
} from '@urbanx/agx.ui-components2';
import {
  TaskCategoryTaskType,
  TaskCategoryWorkflowType,
  useGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client';
import {
  CampaignDetailModelState,
  Deposit,
  DepositType,
  EarlyReleaseOfDeposit,
  StaffGetContractDepositsResponse,
  StaffUpdateContractDepositsRequest,
  ValueType,
  useInvalidateUseStaffGetContractDeposits,
  useStaffGetContractDeposits,
  useStaffUpdateContractDeposits,
} from '@urbanx/agx.campaigns.client';
import { useQueryClient } from '@tanstack/react-query';
import { DepositPanel } from './panels/DepositPanel';
import { AddDepositButton } from './modals/AddDepositButton';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { isEqual } from 'lodash-es';
import { useFormConditionDueDate } from './components/useFormConditionDueDate';
import useRexDisconnectedModal, {
  RexDisconnectedModalChange,
} from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import {
  getDepositExcessAmount,
  getNswBalanceDepositWithDueDate,
  getTotalDepositAmount,
  normalizeDepositToDollar,
} from './depositCalculations';
import EarlyReleaseModal from './modals/EarlyReleaseModal';
import EarlyReleaseSummary from './EarlyReleaseSummary';
import { ContractInformationPage } from './ContractInformationPage';
import { useContractInformationEditor } from './useContractInformationEditor';

const earlyReleaseBlockingTaskTypes = new Set<TaskCategoryTaskType>([
  TaskCategoryTaskType.DepositExceeds10Percent,
  TaskCategoryTaskType.DepositExceeds10PercentPaymentReview,
  TaskCategoryTaskType.EarlyReleaseOfDepositPaymentReview,
  TaskCategoryTaskType.InitialDeposit,
  TaskCategoryTaskType.BalanceDeposit,
]);

const hasDepositValue = (deposit: Deposit | undefined): deposit is Deposit => {
  if (!deposit) return false;

  if (deposit.depositValueType === ValueType.Dollar) {
    return deposit.amount !== undefined && deposit.amount !== null;
  }

  if (deposit.depositValueType === ValueType.Percentage) {
    return deposit.percent !== undefined && deposit.percent !== null;
  }

  return false;
};

const Deposits = () => {
  const { contract, campaignId } = useSelectedContract();
  const { campaign } = useSelectedCampaign();
  const isNsw = campaign?.state === CampaignDetailModelState.NSW;

  const [openEarlyReleaseModal, setOpenEarlyReleaseModal] = useState(false);
  const invalidateUseStaffGetContractDeposits = useInvalidateUseStaffGetContractDeposits();
  const queryClient = useQueryClient();

  const {
    data: contractDeposits,
    isPending,
    isError,
  } = useStaffGetContractDeposits(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const earlyReleaseOfDeposit = contractDeposits?.earlyReleaseOfDeposit;

  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  const { staffUpdateContractDeposits } = useStaffUpdateContractDeposits({
    onSuccess: () => {},
    onError: () => {},
  });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaign?.campaignId || '',
    campaign?.campaignId !== undefined
  );
  const { data: contractTasks } = useGetAutopilotTasksForCampaign(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const hasOutstandingDepositTasks = (contractTasks ?? []).some(
    (task) =>
      task.category?.workflowType === TaskCategoryWorkflowType.IndependentContract &&
      task.isCompleted !== true &&
      earlyReleaseBlockingTaskTypes.has(task.category?.taskType as TaskCategoryTaskType)
  );

  const earlyReleaseActionedBy = (() => {
    const changedByUserId = earlyReleaseOfDeposit?.changeInfo?.changedByUserId;
    return changedByUserId ?? '-';
  })();

  type DepositsDraft = {
    initialDeposit: Deposit | undefined;
    balanceDeposit: Deposit | undefined;
  };

  const toDraft = (deposits: StaffGetContractDepositsResponse): DepositsDraft => ({
    initialDeposit: deposits.deposits?.find(
      (deposit) => deposit.depositType === DepositType.Initial
    ),
    balanceDeposit: deposits.deposits?.find(
      (deposit) => deposit.depositType === DepositType.Balance
    ),
  });

  const source = contractDeposits ? toDraft(contractDeposits) : undefined;

  const editor = useContractInformationEditor({
    resourceKey: `${campaignId ?? ''}:${contract?.contractId ?? ''}:deposits`,
    source,
    isPending,
    isError,
    persist: async ({ baseline, draft }) => {
      const balanceDepositForSave = isNsw
        ? getNswBalanceDepositWithDueDate({
            deposit: draft.balanceDeposit,
            dueDateString: currentInitialAndBalanceDueDates.balance,
          })
        : draft.balanceDeposit;
      const deposits = isNsw
        ? [
            normalizeDepositToDollar(draft.initialDeposit, contract?.salePrice ?? undefined),
            normalizeDepositToDollar(balanceDepositForSave, contract?.salePrice ?? undefined),
          ]
        : [draft.initialDeposit, draft.balanceDeposit];
      const request: StaffUpdateContractDepositsRequest = {
        deposits: deposits.filter(hasDepositValue),
      };
      const response = await staffUpdateContractDeposits({
        body: request,
        contractId: contract!.contractId,
      });

      if (!response?.success) return { accepted: false, errors: response?.errors ?? [] };
      if (!response.contractDeposits) throw new Error('Canonical contract deposits are required');

      queryClient.setQueryData<StaffGetContractDepositsResponse>(
        ['staffGetContractDeposits', campaignId!, contract!.contractId],
        response.contractDeposits
      );

      if (!rexListing && isPendingRexListing === false) {
        const changes: RexDisconnectedModalChange[] = [];

        if (!isEqual(baseline.initialDeposit?.amount, draft.initialDeposit?.amount)) {
          changes.push({
            field: RexDisconnectedModalFields.InitialDeposit,
            value: draft.initialDeposit?.amount,
          });
        }
        if (!isEqual(previousDueDates.initial, currentInitialAndBalanceDueDates.initial)) {
          changes.push({
            field: RexDisconnectedModalFields.InitialDepositDueDate,
            value: currentInitialAndBalanceDueDates.initial,
          });
        }
        if (!isEqual(baseline.balanceDeposit?.amount, draft.balanceDeposit?.amount)) {
          changes.push({
            field: RexDisconnectedModalFields.BalanceDeposit,
            value: draft.balanceDeposit?.amount,
          });
        }
        if (!isEqual(previousDueDates.balance, currentInitialAndBalanceDueDates.balance)) {
          changes.push({
            field: RexDisconnectedModalFields.BalanceDepositDueDate,
            value: currentInitialAndBalanceDueDates.balance,
          });
        }

        openWithChanges(changes);
      }

      return { accepted: true, baseline: toDraft(response.contractDeposits) };
    },
    refresh: {
      authority: 'mutation',
      refreshSource: () => invalidateUseStaffGetContractDeposits(campaignId!, contract!.contractId),
    },
  });

  const previousInitialDueDate = useFormConditionDueDate(source?.initialDeposit);
  const previousBalanceDueDate = useFormConditionDueDate(source?.balanceDeposit);
  const currentInitialDueDate = useFormConditionDueDate(editor.draft?.initialDeposit);
  const currentBalanceDueDate = useFormConditionDueDate(editor.draft?.balanceDeposit);
  const previousDueDates = {
    initial: previousInitialDueDate.dueDateString,
    balance: previousBalanceDueDate.dueDateString,
  };
  const currentInitialAndBalanceDueDates = {
    initial: currentInitialDueDate.dueDateString,
    balance: currentBalanceDueDate.dueDateString,
  };

  if (!editor.draft) {
    return (
      <ContractInformationPage currentPage={Pages.DEPOSITS} editor={editor}>
        {null}
      </ContractInformationPage>
    );
  }

  const { initialDeposit, balanceDeposit } = editor.draft;
  const totalDeposit = getTotalDepositAmount({
    initialDeposit,
    balanceDeposit,
    salePrice: contract?.salePrice ?? undefined,
  });
  const depositExcessAmount = getDepositExcessAmount({
    totalDepositAmount: totalDeposit,
    salePrice: contract?.salePrice,
  });
  const hasInitialDepositValue = hasDepositValue(initialDeposit);
  const hasBalanceDepositValue = hasDepositValue(balanceDeposit);
  const canAddDeposit =
    !hasInitialDepositValue || (isNsw && (!hasInitialDepositValue || !hasBalanceDepositValue));

  return (
    <ContractInformationPage currentPage={Pages.DEPOSITS} editor={editor}>
      {!isPending ? (
        <>
          <AgxSpacer direction="vertical" fill gap={'2xs'}>
            {depositExcessAmount != null && depositExcessAmount > 0 && (
              <AgxAlerts
                alerts={[
                  {
                    alertType: 'Error',
                    heading: `Deposit exceeds 10% of the sale price by ${formatCurrency(depositExcessAmount, 2)}`,
                    id: 'depositExceedErrorAlert',
                    isDismissed: false,
                    title: '',
                    description: '',
                    solveElementId: '',
                  },
                ]}
                id={'depositExeedsAlert'}
                dismissedAlertIds={[]}
                onValueChanged={() => {}}
              />
            )}
            {!hasInitialDepositValue && !hasBalanceDepositValue && (
              <SubPagePanel expandable={false}>
                <AgxSpacer
                  direction="horizontal"
                  gap="spacing-md"
                  contentAlignment="center"
                  contentJustification="center"
                  fill
                  padding={{ top: 'spacing-8xl', bottom: 'spacing-8xl' }}
                >
                  <AgxTypography id="no-deposits" type="body_ui" size="medium">
                    No deposits added.
                  </AgxTypography>
                </AgxSpacer>
              </SubPagePanel>
            )}
            <AgxSpacer direction="vertical" fill gap={'md'}>
              {hasInitialDepositValue && (
                <DepositPanel
                  id="initial-deposit-panel"
                  isNsw={isNsw}
                  panelTitle="Initial Deposit"
                  deposit={initialDeposit}
                  onChange={(value) =>
                    editor.update('initialDeposit', value as Deposit | undefined)
                  }
                />
              )}
              {hasBalanceDepositValue && (
                <DepositPanel
                  id="balance-deposit-panel"
                  isNsw={isNsw}
                  panelTitle="Balance Deposit"
                  deposit={balanceDeposit}
                  onChange={(value) =>
                    editor.update('balanceDeposit', value as Deposit | undefined)
                  }
                />
              )}
              {earlyReleaseOfDeposit && (
                <EarlyReleaseSummary
                  earlyRelease={earlyReleaseOfDeposit as EarlyReleaseOfDeposit}
                  actionedBy={earlyReleaseActionedBy}
                />
              )}
            </AgxSpacer>
          </AgxSpacer>
          <AgxSpacer
            direction="horizontal"
            gap="spacing-md"
            contentJustification="start"
            contentAlignment="start"
          >
            {canAddDeposit && (
              <AddDepositButton
                id="initial-deposit"
                label={isNsw ? 'Add Deposit' : 'Add Initial Deposit'}
                depositType={DepositType.Initial}
                onAddDeposits={(initial, balance) => {
                  editor.update((current) => ({
                    ...current,
                    initialDeposit: initial,
                    ...(isNsw || balance ? { balanceDeposit: balance } : {}),
                  }));
                }}
              />
            )}
            {!isNsw && !hasBalanceDepositValue && (
              <AddDepositButton
                id="balance-deposit"
                label="Add Balance Deposit"
                depositType={DepositType.Balance}
                onAddDeposits={(_, balance) => {
                  if (balance) {
                    editor.update('balanceDeposit', balance);
                  }
                }}
                disabled={!hasInitialDepositValue}
              />
            )}
            {!earlyReleaseOfDeposit && (
              <AgxButton
                id="early-release"
                text="Early Release"
                variant="primary"
                danger
                disabled={hasOutstandingDepositTasks || contractTasks == null}
                onClick={() => setOpenEarlyReleaseModal(true)}
              />
            )}
          </AgxSpacer>
        </>
      ) : undefined}
      {openEarlyReleaseModal && (
        <EarlyReleaseModal
          isOpen={openEarlyReleaseModal}
          onClose={() => setOpenEarlyReleaseModal(false)}
        />
      )}
      {rexDisconnectedModal}
    </ContractInformationPage>
  );
};

export default Deposits;
